import type {
  CampaignCreationCapabilities,
  SmartCreationStrategyDiagnostic,
  SmartCreationStrategyRecommendationEvidence,
  SmartCreationTargetingStrategyKind,
} from "@adpilot/shared";
import { IconDatabase, IconEyeOff, IconRestore } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { StrategyEvidenceDetails } from "@/components/smart-creation/StrategyEvidenceDetails";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { Popover, PopoverPopup, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  allowedMatchTypes,
  diagnosticTargetId,
  type EditableTargetValue,
  sourceLabelKey,
  type TargetRow,
  targetLabel,
} from "./strategy-target-model";

interface TargetInspectorProps {
  selectedTarget: TargetRow;
  branchKind: SmartCreationTargetingStrategyKind;
  capabilities: CampaignCreationCapabilities;
  mutationPending: boolean;
  selectedBidInvalid: boolean;
  recommendationEvidence: SmartCreationStrategyRecommendationEvidence | null;
  recommendationAcceptAllowed: boolean;
  restoreAllowed: boolean;
  branchDiagnostics: SmartCreationStrategyDiagnostic[];
  onUpdateTarget: (id: string, patch: Partial<EditableTargetValue>) => void;
  onRemoveLocalTarget: (id: string) => void;
  onAcceptRecommendation: () => Promise<void>;
}

export function TargetInspector({
  selectedTarget,
  branchKind,
  capabilities,
  mutationPending,
  selectedBidInvalid,
  recommendationEvidence,
  recommendationAcceptAllowed,
  restoreAllowed,
  branchDiagnostics,
  onUpdateTarget,
  onRemoveLocalTarget,
  onAcceptRecommendation,
}: TargetInspectorProps) {
  const { t } = useTranslation("smartCreation");
  return (
    <FieldGroup className="grid gap-3 md:grid-cols-3">
      <div className="md:col-span-3">
        <h3 id="smart-creation-target-inspector-title" className="font-semibold text-ink">
          {t("strategy.editor.inspectorTitle")}
        </h3>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {t("strategy.editor.inspectorDescription")}
        </p>
      </div>
      <Field>
        <FieldLabel>{t("strategy.editor.target")}</FieldLabel>
        <p className="break-words text-sm font-medium text-ink">
          {targetLabel(selectedTarget.intent)}
        </p>
      </Field>
      {selectedTarget.intent.type === "keyword" ? (
        <Field>
          <FieldLabel htmlFor="smart-creation-target-match">
            {t("strategy.editor.matchType")}
          </FieldLabel>
          <Select
            disabled={
              mutationPending ||
              branchKind === "automatic" ||
              Boolean(selectedTarget.candidate && !selectedTarget.local)
            }
            items={allowedMatchTypes(branchKind).map((value) => ({
              value,
              label: value,
            }))}
            value={selectedTarget.intent.matchType}
            onValueChange={(value) => {
              if (!value || selectedTarget.intent.type !== "keyword") return;
              onUpdateTarget(selectedTarget.id, {
                intent: {
                  ...selectedTarget.intent,
                  matchType: value as "exact" | "phrase" | "broad",
                },
              });
            }}
          >
            <SelectTrigger
              id="smart-creation-target-match"
              className="w-full"
              disabled={
                branchKind === "automatic" ||
                Boolean(selectedTarget.candidate && !selectedTarget.local)
              }
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {allowedMatchTypes(branchKind).map((value) => (
                  <SelectItem key={value} value={value} className="capitalize">
                    {value}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      ) : null}
      <Field invalid={selectedBidInvalid}>
        <FieldLabel htmlFor="smart-creation-target-bid">{t("strategy.editor.bid")}</FieldLabel>
        <InputGroup>
          <InputGroupAddon>
            <InputGroupText>{capabilities.currencyCode}</InputGroupText>
          </InputGroupAddon>
          <InputGroupInput
            id="smart-creation-target-bid"
            type="number"
            aria-invalid={selectedBidInvalid}
            disabled={
              branchKind === "automatic" ||
              Boolean(selectedTarget.candidate && !selectedTarget.local)
            }
            min={capabilities.bid.min}
            max={capabilities.bid.max}
            step={1 / 10 ** capabilities.bid.decimals}
            value={selectedTarget.bid}
            onChange={(event) => {
              const bid = Number(event.currentTarget.value);
              if (Number.isFinite(bid)) onUpdateTarget(selectedTarget.id, { bid });
            }}
          />
        </InputGroup>
        <FieldDescription>
          {capabilities.bid.min.toFixed(capabilities.bid.decimals)}–
          {capabilities.bid.max.toFixed(capabilities.bid.decimals)} {capabilities.currencyCode}
        </FieldDescription>
      </Field>
      <div className="rounded-md bg-subtle p-3 text-xs">
        <div className="flex items-center gap-2 font-medium text-ink">
          <IconDatabase className="size-4" />
          {t("strategy.editor.source")}
        </div>
        <p className="mt-1 text-muted-foreground">{t(sourceLabelKey(selectedTarget))}</p>
        {selectedTarget.candidate ||
        (selectedTarget.persisted?.provenance.mode !== "entered" && selectedTarget.persisted) ? (
          <Popover>
            <PopoverTrigger
              render={<Button type="button" variant="link" size="sm" className="mt-1 px-0" />}
            >
              {t("strategy.editor.auditDetails")}
            </PopoverTrigger>
            <PopoverPopup className="w-80 p-4">
              <PopoverTitle>{t("strategy.editor.auditDetails")}</PopoverTitle>
              <p className="mt-2 text-xs font-medium">
                {selectedTarget.candidate
                  ? t(`strategy.editor.sources.${selectedTarget.candidate.source}`)
                  : t(sourceLabelKey(selectedTarget))}
              </p>
              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {selectedTarget.candidate?.rationale ??
                  (selectedTarget.persisted?.provenance.mode !== "entered"
                    ? selectedTarget.persisted?.provenance.originalRecommendation.rationale
                    : null)}
              </p>
            </PopoverPopup>
          </Popover>
        ) : (
          <p className="mt-2 leading-5 text-muted-foreground">{t("strategy.editor.noEvidence")}</p>
        )}
      </div>
      {recommendationEvidence ? (
        <Popover>
          <PopoverTrigger render={<Button type="button" variant="ghost" size="sm" />}>
            <IconDatabase data-icon="inline-start" />
            {t("strategy.evidenceDetails")}
          </PopoverTrigger>
          <PopoverPopup className="w-[340px]">
            <div className="flex flex-col gap-3">
              <PopoverTitle>{t("strategy.evidenceDetails")}</PopoverTitle>
              <StrategyEvidenceDetails evidence={recommendationEvidence} />
            </div>
          </PopoverPopup>
        </Popover>
      ) : null}
      {selectedTarget.candidate && !selectedTarget.local && !recommendationAcceptAllowed ? (
        <p className="text-xs text-warning" aria-live="polite">
          {t("strategy.editor.acceptBlockedByChanges")}
        </p>
      ) : null}
      {(!selectedTarget.candidate || selectedTarget.local) && !restoreAllowed ? (
        <p className="text-xs text-warning" aria-live="polite">
          {t("strategy.editor.restoreBlockedByChanges")}
        </p>
      ) : null}
      <div>
        <h4 className="text-xs font-semibold text-ink">{t("strategy.editor.diagnostics")}</h4>
        {branchDiagnostics.filter((item) => item.targetId === diagnosticTargetId(selectedTarget))
          .length > 0 ? (
          <ul className="mt-2 space-y-1 text-xs text-destructive-foreground">
            {branchDiagnostics
              .filter((item) => item.targetId === diagnosticTargetId(selectedTarget))
              .map((item) => (
                <li key={`${item.code}:${item.message}`}>{item.message}</li>
              ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">{t("strategy.editor.noDiagnostics")}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {selectedTarget.candidate && !selectedTarget.local ? (
          <Button
            type="button"
            size="sm"
            disabled={!recommendationAcceptAllowed}
            onClick={() => void onAcceptRecommendation()}
          >
            <IconDatabase data-icon="inline-start" />
            {t("strategy.editor.useRecommendation")}
          </Button>
        ) : null}
        {(!selectedTarget.candidate || selectedTarget.local) && branchKind !== "automatic" ? (
          <Button
            type="button"
            size="sm"
            variant={selectedTarget.included ? "destructive-outline" : "outline"}
            onClick={() => {
              if (selectedTarget.local) {
                onRemoveLocalTarget(selectedTarget.id);
                return;
              }
              onUpdateTarget(selectedTarget.id, { included: !selectedTarget.included });
            }}
          >
            {selectedTarget.included ? (
              <IconEyeOff data-icon="inline-start" />
            ) : (
              <IconRestore data-icon="inline-start" />
            )}
            {t(
              selectedTarget.included
                ? "strategy.editor.excludeTarget"
                : "strategy.editor.restoreExcludedTarget",
            )}
          </Button>
        ) : null}
      </div>
    </FieldGroup>
  );
}
